import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { Send, RefreshCw, CheckCircle2, XCircle, Clock, Calendar, BellRing, MapPin, AlertCircle, Loader2, ExternalLink, Copy } from 'lucide-react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

const TAB_DAY_BEFORE = 'day_before';
const TAB_SAME_DAY = 'same_day';

function formatSentAt(isoStr) {
  if (!isoStr) return null;
  const s = isoStr.endsWith('Z') ? isoStr : isoStr.replace(' ', 'T') + 'Z';
  return new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

function ReminderBadge({ status, sentAt, error }) {
  if (status === 'sent') return (
    <div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" title={sentAt ? `Sent ${formatSentAt(sentAt)}` : 'Sent'}>
        <CheckCircle2 className="w-3 h-3" /> Sent
      </span>
      {sentAt && <p className="text-[10px] text-brand-neutral mt-0.5">{formatSentAt(sentAt)}</p>}
    </div>
  );
  if (status === 'failed') return (
    <div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" title={error || 'Send failed'}>
        <XCircle className="w-3 h-3" /> Failed
      </span>
      {error && <p className="text-[10px] text-red-500 mt-0.5 max-w-[200px] truncate" title={error}>{error}</p>}
    </div>
  );
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
      <AlertCircle className="w-3 h-3" /> Not sent
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function BookedReminders() {
  const [activeTab, setActiveTab] = useState(TAB_DAY_BEFORE);
  const queryClient = useQueryClient();
  const { selectedLocationId, currentLocation } = useLocationContext();

  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const targetDate = activeTab === TAB_DAY_BEFORE ? tomorrow : today;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['scheduled-reminders', activeTab, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/scheduled-reminders/live?date=${targetDate}`);
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      // Refetch latest data before sending
      await refetch();
      const { data } = await axios.post('/api/scheduled-reminders/send', { type: activeTab });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reminders'] });
    },
  });

  const [sendingJobUid, setSendingJobUid] = useState(null);
  const sendOneMutation = useMutation({
    mutationFn: async ({ jobUid, type }) => {
      setSendingJobUid(jobUid);
      const { data } = await axios.post('/api/scheduled-reminders/send-one', { type, jobUid });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reminders'] });
    },
    onSettled: () => setSendingJobUid(null),
  });

  const jobs = data?.jobs || [];
  const times = data?.scheduleTimes || {};
  const scheduleTime = activeTab === TAB_DAY_BEFORE ? times.dayBeforeTime : times.sameDayTime;
  const templateName = activeTab === TAB_DAY_BEFORE ? times.dayBeforeTemplate : times.sameDayTemplate;

  // Derive reminder status per job for the active tab
  const getStatus = (job) => activeTab === TAB_DAY_BEFORE ? job.day_before_status : job.same_day_status;
  const getSentAt = (job) => activeTab === TAB_DAY_BEFORE ? job.day_before_sent_at : job.same_day_sent_at;
  const getError = (job) => activeTab === TAB_DAY_BEFORE ? job.day_before_error : job.same_day_error;

  const sentCount = jobs.filter(j => getStatus(j) === 'sent').length;
  const failedCount = jobs.filter(j => getStatus(j) === 'failed').length;
  const pendingCount = jobs.filter(j => getStatus(j) === 'pending').length;
  const notSentCount = jobs.filter(j => !getStatus(j)).length;
  const totalCount = jobs.length;

  return (
    <Layout>
      {/* Page Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-linear-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <BellRing className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Appointment Alerts</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Automated WhatsApp alerts for scheduled AMC appointments</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-xl text-brand-neutral hover:text-brand-primary hover:bg-brand-light transition-colors disabled:opacity-50"
            title="Refresh from Zuper"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending || totalCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-xl text-sm font-medium hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sendMutation.isPending ? 'Sending...' : `Send ${activeTab === TAB_DAY_BEFORE ? 'Tomorrow' : 'Today'}`}
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-white rounded-xl shadow-sm border border-brand-light/60 w-fit">
            {[
              { key: TAB_DAY_BEFORE, label: 'Tomorrow', date: tomorrow },
              { key: TAB_SAME_DAY, label: 'Today', date: today },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-brand-neutral hover:text-brand-dark'
                }`}
              >
                {tab.label}
                <span className={`ml-2 text-xs ${activeTab === tab.key ? 'text-white/70' : 'text-brand-neutral/60'}`}>{tab.date}</span>
              </button>
            ))}
          </div>

          {/* Schedule info bar */}
          <div className="flex items-center gap-4 px-4 py-3 bg-blue-50 rounded-xl border border-blue-100 text-sm">
            <Clock className="w-4 h-4 text-blue-500 shrink-0" />
            <span className="text-blue-700">
              Auto-sends at <strong>{scheduleTime}</strong> · Template: <strong>{templateName}</strong>
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total Jobs', value: totalCount, color: 'text-brand-dark', bg: 'bg-white' },
              { label: 'Sent', value: sentCount, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Pending / Not Sent', value: pendingCount + notSentCount, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Failed', value: failedCount, color: 'text-red-600', bg: 'bg-red-50' },
            ].map(stat => (
              <div key={stat.label} className={`${stat.bg} rounded-xl p-4 text-center shadow-sm border border-brand-light/40`}>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-brand-neutral mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Jobs Table */}
          <div className="bg-white rounded-2xl border border-brand-light/60 overflow-hidden shadow-sm">
            {isLoading ? (
              <div className="p-12 text-center text-brand-neutral text-sm">Loading...</div>
            ) : jobs.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="w-10 h-10 text-brand-light mx-auto mb-3" />
                <p className="text-brand-neutral text-sm font-medium">
                  No scheduled AMC jobs for {formatDate(targetDate)}
                </p>
                <p className="text-xs text-brand-neutral/60 mt-1">
                  Jobs will appear here once customers confirm their AMC appointment
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-light/60 bg-brand-light/20">
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Job #</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Customer</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Asset</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Slot</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-brand-neutral uppercase tracking-wider">Reminder</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-light/40">
                    {jobs.map(job => {
                      const status = getStatus(job);
                      const sentAt = getSentAt(job);
                      const error = getError(job);
                      return (
                        <tr key={job.job_uid} className="hover:bg-brand-light/10 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-brand-neutral">
                            <div className="inline-flex items-center gap-0.5 group/wo">
                              {job.work_order_number || '—'}
                              <button
                                onClick={() => window.open(`https://web.zuperpro.com/jobs/${job.job_uid}/details`, '_blank')}
                                className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                                title="Open in Zuper"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                              {job.work_order_number && (
                                <button
                                  onClick={() => { navigator.clipboard.writeText(String(job.work_order_number)); }}
                                  className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                                  title="Copy job number"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-brand-dark">{job.customer_name}</p>
                            <p className="text-xs text-brand-neutral">{job.customer_phone || '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-brand-neutral">
                            {job.asset_name ? (
                              <div>
                                <div className="text-brand-dark">{job.asset_code ? `${job.asset_code} - ` : ''}{job.asset_name}</div>
                                {job.asset_serial_number && (
                                  <div className="font-mono text-[11px] text-brand-neutral/70">{job.asset_serial_number}</div>
                                )}
                              </div>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-brand-dark whitespace-nowrap">{formatDate(job.scheduled_date)}</td>
                          <td className="px-4 py-3 text-xs text-brand-neutral capitalize">{job.slot_label || job.scheduled_time || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-brand-neutral bg-brand-light/60 px-2 py-0.5 rounded-full">{job.job_category || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <ReminderBadge status={status} sentAt={sentAt} error={error} />
                          </td>
                          <td className="px-4 py-3">
                            {status !== 'sent' && (
                              <button
                                onClick={() => sendOneMutation.mutate({ jobUid: job.job_uid, type: activeTab })}
                                disabled={sendingJobUid === job.job_uid}
                                title={`Send ${activeTab === TAB_DAY_BEFORE ? 'tomorrow' : 'today'} reminder`}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-primary hover:bg-brand-secondary rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {sendingJobUid === job.job_uid
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Send className="w-3 h-3" />}
                                Send
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Send result feedback */}
          {sendMutation.isSuccess && sendMutation.data?.result && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
              ✓ Sent {sendMutation.data.result.sent}, Failed {sendMutation.data.result.failed}, Skipped {sendMutation.data.result.skipped}
            </div>
          )}
          {sendMutation.isError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              Error: {sendMutation.error?.response?.data?.error || sendMutation.error?.message}
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
}
